import { useState, useCallback, useMemo } from "react";
import {
  Lock,
  LockOpen,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import {
  Property,
  Sector,
} from "grapesjs";
import DebouncedTextField from "features/common/components/DebouncedTextField";
import { useEditor } from "@grapesjs/react";

interface IDimensionFieldsProps {
  sector: Sector;
}

const DimensionFields = ({
  sector,
}: IDimensionFieldsProps) => {
  const editor = useEditor();
  const theme = useTheme();
  const [ratioLocked, setRatioLocked] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);

  const { widthProp, heightProp } = useMemo(() => {
    if (!editor || sector.getId() !== "size") return { widthProp: null, heightProp: null };

    const selected = editor.getSelected();
    if (!selected) return { widthProp: null, heightProp: null };

    let widthProp: Property | null = null;
    let heightProp: Property | null = null;

    sector.getProperties().forEach(property => {
      if (property.get('property') === 'width') {
        widthProp = property;
      }
      if (property.get('property') === 'height') {
        heightProp = property;
      }
    });

    return { widthProp, heightProp } as { widthProp: Property | null, heightProp: Property | null };


  }, [sector, editor]);


  const getWidthValue = useCallback(() => {
    if (!widthProp) return { value: "", unit: "px" };
    const value = widthProp?.getValue();
    if (!value || typeof value !== 'number') {
      return { value: "", unit: "px" };
    }
    const unit = widthProp?.attributes?.unit || "px";
    return { value: value.toString(), unit };
  }, [widthProp]);

  const getHeightValue = useCallback(() => {
    if (!heightProp) return { value: "", unit: "px" };
    const value = heightProp.getValue();
    if (!value || typeof value !== 'number') {
      return { value: "", unit: "px" };
    }
    const unit = heightProp.attributes?.unit || "px";
    return { value: value.toString(), unit };
  }, [heightProp]);

  const widthData = getWidthValue();
  const heightData = getHeightValue();


  const handleRatioLockChange = () => {
    if (!ratioLocked) {
      // Enable ratio lock - calculate current aspect ratio
      const widthValue = parseFloat(widthData.value) || 0;
      const heightValue = parseFloat(heightData.value) || 0;
      if (widthValue > 0 && heightValue > 0) {
        setAspectRatio(widthValue / heightValue);
      }
    }
    setRatioLocked(!ratioLocked);
  };



  const handleWidthChange = (newValue: string) => {
    if (!widthProp || !heightProp) return;
    // Update the property immediately
    if (newValue === "") {
      widthProp.clear();
      if (ratioLocked) {
        heightProp.clear();
      }
    } else {
      const widthValue = newValue === "" ? widthProp.getDefaultValue() : newValue;
      widthProp.upValue(`${widthValue}${widthData.unit}`);

      // If ratio is locked, update height
      if (ratioLocked) {
        const widthNum = parseFloat(widthValue) || 0;
        const newHeightValue = (widthNum / aspectRatio).toString();
        heightProp.upValue(`${newHeightValue}${heightData.unit}`);
      }
    }
  };

  const handleHeightChange = (newValue: string) => {
    if (!widthProp || !heightProp) return;
    // Update the property immediately
    if (newValue === "") {
      heightProp.clear();
      if (ratioLocked) {
        widthProp.clear();
      }
    } else {
      const heightValue = newValue === "" ? heightProp.getDefaultValue() : newValue;
      heightProp.upValue(`${heightValue}${heightData.unit}`);

      // If ratio is locked, update width
      if (ratioLocked) {
        const heightNum = parseFloat(heightValue) || 0;
        const newWidthValue = (heightNum * aspectRatio).toString();
        widthProp.upValue(`${newWidthValue}${widthData.unit}`);
      }
    }
  };

  const handleWidthUnitChange = (newUnit: string) => {
    if (!widthProp || !heightProp) return;
    widthProp.upValue(`${widthData.value}${newUnit}`);
  };

  const handleHeightUnitChange = (newUnit: string) => {
    if (!widthProp || !heightProp) return;
    heightProp.upValue(`${heightData.value}${newUnit}`);
  };

  return (
    <Stack spacing={2}>
      <Typography color="secondary" gutterBottom>
        Dimensions
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
        <Box  >
          <Tooltip title={ratioLocked ? "Unlock aspect ratio" : "Lock aspect ratio"}>
            <IconButton
              size="small"
              onClick={handleRatioLockChange}
              color={ratioLocked ? "primary" : "default"}
              sx={{
                border: ratioLocked ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
              }}
            >
              {ratioLocked ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Stack spacing={2}>
          <DebouncedTextField
            label="Width"
            value={widthData.value}
            onChange={handleWidthChange}
            size="small"
            fullWidth
            type="number"
            debounceMs={300}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <DebouncedTextField
                    select
                    value={widthData.unit}
                    onChange={handleWidthUnitChange}
                    size="small"
                    variant="standard"
                    debounceMs={0}
                    sx={{
                      width: 40,
                      "& .MuiSelect-select": {
                        padding: 0,
                        textAlign: "center",
                      },
                    }}
                  >
                    {["px", "%"].map((unitOption) => (
                      <MenuItem key={unitOption} value={unitOption}>
                        {unitOption}
                      </MenuItem>
                    ))}
                  </DebouncedTextField>
                </InputAdornment>
              ),
            }}
          />

          <DebouncedTextField
            label="Height"
            value={heightData.value}
            onChange={handleHeightChange}
            size="small"
            fullWidth
            type="number"
            debounceMs={300}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <DebouncedTextField
                    select
                    value={heightData.unit}
                    onChange={handleHeightUnitChange}
                    size="small"
                    variant="standard"
                    debounceMs={0}
                    sx={{
                      width: 40,
                      "& .MuiSelect-select": {
                        padding: 0,
                        textAlign: "center",
                      },
                    }}
                  >
                    {["px", "%"].map((unitOption) => (
                      <MenuItem key={unitOption} value={unitOption}>
                        {unitOption}
                      </MenuItem>
                    ))}
                  </DebouncedTextField>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default DimensionFields; 